import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import React, { Suspense, lazy } from 'react';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ProtectedRoute from '../core/routing/ProtectedRoute';
import { AuthCheck } from '../core/authentication/AuthCheck';
import { useAuthStore } from '../core/authentication/authStore';

// Lazy load components
const AuthLayout = lazy(() => import('../layout/AuthLayout'));
const MainLayout = lazy(() => import('../layout/MainLayout'));
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage'));
const AdminDashboard = lazy(() => import('../pages/admin/AdminDashboard'));
const AccountsManagement = lazy(() => import('../pages/admin/AccountsManagement'));
const LotteryManagement = lazy(() => import('../pages/admin/LotteryManagement'));
const CommissionManagement = lazy(() => import('../pages/admin/CommissionManagement'));
const FinancialManagement = lazy(() => import('../pages/admin/FinancialManagement'));
const BetManagement = lazy(() => import('../pages/admin/BetManagement'));
const AdminSettings = lazy(() => import('../pages/admin/AdminSettings'));
const AgentDashboard = lazy(() => import('../pages/agent/AgentDashboard'));
const AgentSettings = lazy(() => import('../pages/agent/AgentSettings'));
const UserDashboard = lazy(() => import('../pages/user/UserDashboard'));
const UserStats = lazy(() => import('../pages/user/UserStats'));
const UserSettings = lazy(() => import('../pages/user/UserSettings'));
const NotFound = lazy(() => import('../pages/NotFound'));

// Loading component
const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingSpinner />}>
    {children}
  </Suspense>
);

// Root redirect component with proper hydration
const RootRedirect = () => {
  const { isAuthenticated, user } = useAuthStore();

  // If authenticated and user exists, redirect to appropriate dashboard
  if (isAuthenticated && user) {
    const redirectPath = user.type === 'admin' ? '/admin/dashboard' :
                        user.type === 'agent' ? '/agent/dashboard' :
                        '/user/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  // Otherwise, redirect to login
  return <Navigate to="/auth/login" replace />;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <LazyWrapper><RootRedirect /></LazyWrapper>
  },
  {
    path: '/auth',
    element: <LazyWrapper><AuthLayout /></LazyWrapper>,
    children: [
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'register',
        element: <RegisterPage />,
      },
    ],
  },
  {
    path: '/admin',
    element: (
      <ProtectedRoute requiredRole="admin">
        <LazyWrapper>
          <MainLayout />
        </LazyWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/admin/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: <AdminDashboard />,
      },
      {
        path: 'accounts',
        element: <AccountsManagement />,
      },
      {
        path: 'lottery',
        element: <LotteryManagement />,
      },
      {
        path: 'commissions',
        element: <CommissionManagement />,
      },
      {
        path: 'financial',
        element: <FinancialManagement />,
      },
      {
        path: 'bets',
        element: <BetManagement />,
      },
      {
        path: 'settings',
        element: <AdminSettings />,
      },
    ],
  },
  {
    path: '/agent',
    element: (
      <ProtectedRoute requiredRole="agent">
        <LazyWrapper>
          <MainLayout />
        </LazyWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/agent/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: <AgentDashboard />,
      },
      {
        path: 'settings',
        element: <AgentSettings />,
      },
    ],
  },
  {
    path: '/user',
    element: (
      <ProtectedRoute requiredRole="user">
        <LazyWrapper>
          <MainLayout />
        </LazyWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/user/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: <UserDashboard />,
      },
      {
        path: 'stats',
        element: <UserStats />,
      },
      {
        path: 'settings',
        element: <UserSettings />,
      },
    ],
  },
  {
    path: '*',
    element: (
      <LazyWrapper>
        <NotFound />
      </LazyWrapper>
    ),
  },
]);

export const AppRouter = () => (
  <AuthCheck>
    <RouterProvider router={router} />
  </AuthCheck>
);